import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Media {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  alt_text: string;
  created_at: string;
}

export default function MediaLibrary() {
  const [media, setMedia] = useState<Media[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const { data, error } = await supabase
        .from('media_library')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMedia(data || []);
    } catch (error) {
      console.error('Medya yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `uploads/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(filePath);

      const { error: dbError } = await supabase
        .from('media_library')
        .insert([{
          file_name: file.name,
          file_url: publicUrl,
          file_type: file.type,
          file_size: file.size,
          alt_text: file.name,
        }]);

      if (dbError) throw dbError;

      alert('Dosya yüklendi!');
      fetchMedia();
    } catch (error) {
      console.error('Yükleme hatası:', error);
      alert('Dosya yüklenirken bir hata oluştu!');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string, fileUrl: string) => {
    if (!confirm('Bu dosyayı silmek istediğinize emin misiniz?')) return;

    try {
      const filePath = fileUrl.split('/').slice(-2).join('/');
      
      await supabase.storage
        .from('media')
        .remove([filePath]);

      const { error } = await supabase
        .from('media_library')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('Dosya silindi!');
      fetchMedia();
    } catch (error) {
      console.error('Silme hatası:', error);
      alert('Bir hata oluştu!');
    }
  };

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url);
    alert('URL kopyalandı!');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Medya Kütüphanesi</h1>
          <p className="text-gray-600 mt-2">Görselleri ve dosyaları yönetin</p>
        </div>
        <label className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap">
          <i className="ri-upload-2-line"></i>
          {uploading ? 'Yükleniyor...' : 'Dosya Yükle'}
          <input
            type="file"
            onChange={handleFileUpload}
            disabled={uploading}
            className="hidden"
            accept="image/*"
          />
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        {media.map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedMedia(item)}
          >
            <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
              {item.file_type?.startsWith('image/') ? (
                <img
                  src={item.file_url}
                  alt={item.alt_text}
                  className="w-full h-full object-cover object-top"
                />
              ) : (
                <i className="ri-file-line text-5xl text-gray-400"></i>
              )}
            </div>
            <div className="p-4">
              <p className="text-sm font-medium text-gray-900 truncate mb-1">
                {item.file_name}
              </p>
              <p className="text-xs text-gray-500">
                {formatFileSize(item.file_size)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {media.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-xl">
          Henüz dosya yüklenmemiş. Dosya yüklemek için yukarıdaki butona tıklayın.
        </div>
      )}

      {selectedMedia && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedMedia(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Dosya Detayları</h2>
              <button
                onClick={() => setSelectedMedia(null)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2 md:col-span-1">
                  <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center">
                    {selectedMedia.file_type?.startsWith('image/') ? (
                      <img
                        src={selectedMedia.file_url}
                        alt={selectedMedia.alt_text}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <i className="ri-file-line text-8xl text-gray-400"></i>
                    )}
                  </div>
                </div>

                <div className="col-span-2 md:col-span-1 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dosya Adı
                    </label>
                    <p className="text-gray-900">{selectedMedia.file_name}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dosya Boyutu
                    </label>
                    <p className="text-gray-900">{formatFileSize(selectedMedia.file_size)}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dosya Tipi
                    </label>
                    <p className="text-gray-900">{selectedMedia.file_type}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Yüklenme Tarihi
                    </label>
                    <p className="text-gray-900">
                      {new Date(selectedMedia.created_at).toLocaleString('tr-TR')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={selectedMedia.file_url}
                        readOnly
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(selectedMedia.file_url)}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        handleDelete(selectedMedia.id, selectedMedia.file_url);
                        setSelectedMedia(null);
                      }}
                      className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line mr-2"></i>
                      Dosyayı Sil
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
